import React, { useState } from 'react';
import styled from 'styled-components';
import { EuiIcon } from '@elastic/eui';
import FillBar from '../../../../components/metrics/FillBar';
import utilsService from '../../../../../servises/utils.service';
import TinyLineChart from '../../../../components/charts/TinyLineChart';
import { getConnectionsData } from './CommonFuncs';

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 12px;
  margin: 10px 10px 18px 10px;
  width: calc(100% - 20px);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const OverviewCard = styled.div`
  background: linear-gradient(135deg, rgb(35, 37, 47) 0%, rgb(24, 25, 30) 100%);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.25);
  transition: all 0.25s ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px 0 rgba(0, 0, 0, 0.35);
    border-color: ${props => props.accentColor || 'rgba(0, 128, 213, 0.3)'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: ${props => props.accentColor || 'rgb(0, 128, 213)'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding-bottom: 8px;

  h3 {
    font-size: 13px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);

  span.val {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
    font-family: Consolas, monospace;
    background: rgba(255, 255, 255, 0.04);
    padding: 2px 6px;
    border-radius: 4px;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.4);
  transition: color 0.2s;

  &:hover {
    color: rgb(0, 128, 213);
  }
`;

const TinyChartWrapper = styled.div`
  margin-top: 8px;
  border-top: 1px dashed rgba(255, 255, 255, 0.05);
  padding-top: 8px;
`;

const MongoOverviewCards = ({ server, metrics }) => {
    const [copied, setCopied] = useState(false);
    if (!server || !server.metric) return null;

    const {
        networkBytesIn,
        networkBytesOut,
        metric: { serverStatus },
        networkNumRequests,
        totalSizeFormated,
        totalDbUsageFormated,
        totalSizeMb,
        totalDbUsage
    } = server;

    const { connections, storageEngine } = serverStatus;

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const connectionsChart = getConnectionsData(metrics);

    return (
        <OverviewGrid>
            {/* Overview & Node Info */}
            <OverviewCard accentColor="rgb(0, 128, 213)">
                <CardHeader>
                    <h3><EuiIcon type="securitySignal" size="m"/> Instance Info</h3>
                    <EuiIcon type="logoMongodb" size="m" style={{ opacity: 0.6 }}/>
                </CardHeader>
                <CardContent>
                    <InfoRow>
                        <span>Host:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span className="val" title={serverStatus.host}>{serverStatus.host}</span>
                            <CopyButton onClick={() => handleCopy(serverStatus.host)}>
                                <EuiIcon type={copied ? "check" : "copy"} size="s"/>
                            </CopyButton>
                        </div>
                    </InfoRow>
                    <InfoRow>
                        <span>Version:</span>
                        <span className="val">{serverStatus.version}</span>
                    </InfoRow>
                    <InfoRow>
                        <span>Uptime:</span>
                        <span className="val">{utilsService.formatSecondsToDate(serverStatus.uptime)}</span>
                    </InfoRow>
                    <InfoRow>
                        <span>Active:</span>
                        <span className="val">{server.timeAgo}</span>
                    </InfoRow>
                </CardContent>
            </OverviewCard>

            {/* Connections */}
            <OverviewCard accentColor="#50bc5e">
                <CardHeader>
                    <h3><EuiIcon type="users" size="m"/> Connections</h3>
                    <span style={{ fontSize: '11px', color: '#50bc5e', fontWeight: 600 }}>Active</span>
                </CardHeader>
                <CardContent style={{ gap: '4px' }}>
                    <FillBar
                        name=""
                        nameOvveride={null}
                        total={connections.available}
                        totalUnit=""
                        current={connections.current}
                        currentUnit=""
                        originalTotal={connections.available}
                        originalCurrent={connections.current}
                    />
                    {connectionsChart && (
                        <TinyChartWrapper>
                            <TinyLineChart data={connectionsChart} />
                        </TinyChartWrapper>
                    )}
                </CardContent>
            </OverviewCard>

            {/* Storage */}
            <OverviewCard accentColor="#f9be49">
                <CardHeader>
                    <h3><EuiIcon type="storage" size="m"/> Storage</h3>
                    <span style={{
                        fontSize: '9px',
                        background: 'rgba(249, 190, 73, 0.15)',
                        color: '#f9be49',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontWeight: 600
                    }}>
                        {storageEngine.name}
                    </span>
                </CardHeader>
                <CardContent>
                    <InfoRow style={{ marginBottom: '4px' }}>
                        <span>Persistent:</span>
                        <span className="val" style={{ color: storageEngine.persistent ? '#50bc5e' : '#F86B63' }}>
                            {storageEngine.persistent ? 'Yes' : 'No'}
                        </span>
                    </InfoRow>
                    <FillBar
                        name=""
                        total={totalSizeFormated.value}
                        totalUnit={totalSizeFormated.unit}
                        current={totalDbUsageFormated.value}
                        currentUnit={totalDbUsageFormated.unit}
                        originalTotal={totalSizeMb}
                        originalCurrent={totalDbUsage / 1024 / 1024}
                    />
                </CardContent>
            </OverviewCard>

            {/* Network & Requests */}
            <OverviewCard accentColor="#F86B63">
                <CardHeader>
                    <h3><EuiIcon type="globe" size="m"/> Network Traffic</h3>
                    <EuiIcon type="subnetwork" size="m" style={{ opacity: 0.6 }}/>
                </CardHeader>
                <CardContent>
                    <InfoRow>
                        <span>Requests:</span>
                        <span className="val" style={{ color: '#F86B63' }}>{networkNumRequests}</span>
                    </InfoRow>
                    <InfoRow>
                        <span>Data Received:</span>
                        <span className="val">{networkBytesIn.value} {networkBytesIn.unit}</span>
                    </InfoRow>
                    <InfoRow>
                        <span>Data Sent:</span>
                        <span className="val">{networkBytesOut.value} {networkBytesOut.unit}</span>
                    </InfoRow>
                </CardContent>
            </OverviewCard>
        </OverviewGrid>
    );
};

export default MongoOverviewCards;
